import AdminShell from './AdminShell';
/**
 * Редактор модуля (урок + тест)
 */
import { useState, useEffect, useRef } from 'react';
import QuizEditor from "./QuizEditor";
import { createLesson, updateLesson, getLesson, getQuizzes, uploadFile, Module, QuizListItem } from '../../lib/admin-api';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { lessonMarkdownComponents } from '../../lib/lesson-markdown';

/** Как показывать урок при правке: только разметка, разметка и вид рядом, только вид (09.09.2026). */
type LessonView = 'text' | 'split' | 'preview';

/** Кадр-постер из видео прямо в браузере (2-я секунда): серверу ffmpeg не нужен */
function makePoster(file: File): Promise<Blob | null> {
  return new Promise((resolve) => {
    const v = document.createElement('video');
    v.muted = true; v.playsInline = true; v.preload = 'auto';
    const url = URL.createObjectURL(file);
    let done = false;
    const finish = (b: Blob | null) => { if (done) return; done = true; URL.revokeObjectURL(url); resolve(b); };
    v.onloadedmetadata = () => { v.currentTime = Math.min(2, Math.max(0, v.duration - 0.1)); };
    v.onseeked = () => {
      const c = document.createElement('canvas');
      c.width = v.videoWidth; c.height = v.videoHeight;
      c.getContext('2d')?.drawImage(v, 0, 0);
      c.toBlob((b) => finish(b), 'image/jpeg', 0.85);
    };
    v.onerror = () => finish(null);
    setTimeout(() => finish(null), 20000);
    v.src = url;
  });
}

const TRANSLIT: Record<string, string> = {
  а: 'a', б: 'b', в: 'v', г: 'g', д: 'd', е: 'e', ё: 'yo', ж: 'zh', з: 'z', и: 'i', й: 'y', к: 'k', л: 'l', м: 'm',
  н: 'n', о: 'o', п: 'p', р: 'r', с: 's', т: 't', у: 'u', ф: 'f', х: 'h', ц: 'ts', ч: 'ch', ш: 'sh', щ: 'sch',
  ъ: '', ы: 'y', ь: '', э: 'e', ю: 'yu', я: 'ya',
};
function slugify(s: string): string {
  return s.toLowerCase().split('').map((ch) => TRANSLIT[ch] ?? ch).join('')
    .replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 50) || 'video';
}

interface ModuleEditorProps {
  courseId: string;
  initialSection?: 'lesson' | 'quiz';
  module: Module;
  onSave: (module: Module) => void;
  onClose: () => void;
}

export default function ModuleEditor({ courseId, module, onSave, onClose, initialSection = 'lesson' }: ModuleEditorProps) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [name, setName] = useState(module.name);
  const [description, setDescription] = useState(module.description || '');

  const [hasLesson, setHasLesson] = useState(!!module.lesson);
  const [lessonTitle, setLessonTitle] = useState('');
  const [lessonContent, setLessonContent] = useState('');
  const [keyPoints, setKeyPoints] = useState<string[]>([]);
  const [goal, setGoal] = useState('');
  const [outcomes, setOutcomes] = useState<string[]>([]);
  const [newOutcome, setNewOutcome] = useState('');
  const [newKeyPoint, setNewKeyPoint] = useState('');
  const [existingLessonId, setExistingLessonId] = useState<string | null>(null);

  const [quizId, setQuizId] = useState(module.quizIds?.[0] || '');
  const [quizzes, setQuizzes] = useState<QuizListItem[]>([]);
  const [quizSearch, setQuizSearch] = useState('');
  const [editingQuiz, setEditingQuiz] = useState(false);
  // Вид правки урока и текст для предпросмотра с задержкой (09.09.2026, просьба владельца)
  const [lessonView, setLessonView] = useState<LessonView>(() => {
    try { const v = localStorage.getItem('beshek_lesson_view'); return v === 'split' || v === 'preview' ? v : 'text'; } catch { return 'text'; }
  });
  const changeView = (v: LessonView) => {
    setLessonView(v);
    try { localStorage.setItem('beshek_lesson_view', v); } catch { /* нет localStorage */ }
  };
  const [previewText, setPreviewText] = useState('');
  const [uploading, setUploading] = useState<string | null>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const videoInputRef = useRef<HTMLInputElement>(null);

  const toast = (message: string, type: 'success' | 'error' = 'success') =>
    window.dispatchEvent(new CustomEvent('app-toast', { detail: { message, type } }));

  /** Вставить блок в текст урока там, где стоит курсор, отдельным абзацем */
  const insertBlock = (block: string) => {
    const ta = textareaRef.current;
    const pos = ta ? ta.selectionStart : lessonContent.length;
    const before = lessonContent.slice(0, pos).replace(/\n*$/, '');
    const after = lessonContent.slice(pos).replace(/^\n*/, '');
    const next = (before ? before + '\n\n' : '') + block + '\n\n' + after;
    setLessonContent(next);
    requestAnimationFrame(() => {
      if (!ta) return;
      const at = (before ? before.length + 2 : 0) + block.length;
      ta.focus(); ta.setSelectionRange(at, at);
    });
  };

  const onPhotoChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    const caption = window.prompt('Подпись к фото (что на снимке — её видно под фото и читают поисковики)', '') ?? '';
    setUploading('фото');
    try {
      const { url } = await uploadFile(file, { courseId, kind: 'image' });
      insertBlock(`![${caption.replace(/[\[\]]/g, '')}](${url})`);
      toast('Фото загружено и вставлено');
    } catch (err: any) { toast('Фото не загрузилось: ' + (err?.message || err), 'error'); }
    finally { setUploading(null); }
  };

  const onVideoChosen = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]; e.target.value = '';
    if (!file) return;
    const ext = /\.webm$/i.test(file.name) ? '.webm' : '.mp4';
    const base = `${slugify(file.name.replace(/\.[^.]+$/, ''))}-${Date.now().toString(36)}`;
    setUploading('видео');
    try {
      const { absoluteUrl } = await uploadFile(file, { courseId, kind: 'video', name: base + ext });
      const poster = await makePoster(file);
      if (poster) await uploadFile(poster, { courseId, kind: 'poster', name: base + '.jpg', filename: base + '.jpg' });
      insertBlock(absoluteUrl);
      toast(poster ? 'Видео загружено, заставка снята, адрес вставлен' : 'Видео загружено (заставку снять не удалось — плеер будет тёмным)');
    } catch (err: any) { toast('Видео не загрузилось: ' + (err?.message || err), 'error'); }
    finally { setUploading(null); }
  };

  // предпросмотр обновляем с задержкой: на длинном уроке перерисовка на каждую букву тормозит
  useEffect(() => {
    if (lessonView === 'text') return;
    const t = setTimeout(() => setPreviewText(lessonContent), 300);
    return () => clearTimeout(t);
  }, [lessonContent, lessonView]);

  const onVideoLink = () => {
    const u = window.prompt('Адрес видео на RuTube, ВК или YouTube — станет проигрывателем', 'https://');
    if (!u || !/^https:\/\/\S+$/.test(u.trim()) || u.trim() === 'https://') return;
    insertBlock(u.trim());
  };

  useEffect(() => {
    if (module.lesson?.id) {
      setLoading(true);
      getLesson(module.lesson.id)
        .then(lesson => {
          setLessonTitle(lesson.title);
          setLessonContent(lesson.content?.text || '');
          setKeyPoints(lesson.content?.keyPoints || []);
          setGoal((lesson.content as any)?.goal || '');
          setOutcomes((lesson.content as any)?.outcomes || []);
          setExistingLessonId(lesson.id);
        })
        .catch(err => console.error('Failed to load lesson:', err))
        .finally(() => setLoading(false));
    }
  }, [module.lesson?.id]);

  useEffect(() => {
    getQuizzes()
      .then(setQuizzes)
      .catch(err => console.error('Failed to load quizzes:', err));
  }, []);

  const addKeyPoint = () => {
    if (newKeyPoint.trim()) {
      setKeyPoints([...keyPoints, newKeyPoint.trim()]);
      setNewKeyPoint('');
    }
  };

  const removeKeyPoint = (index: number) => {
    setKeyPoints(keyPoints.filter((_, i) => i !== index));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let lessonData = module.lesson;

      if (hasLesson) {
        const lessonPayload = {
          collectionId: courseId,
          moduleId: module.id,
          title: lessonTitle || name,
          type: 'text',
          content: {
            text: lessonContent,
            format: 'markdown',
            keyPoints,
            examples: [],
            goal: goal.trim() || undefined,
            outcomes: outcomes.filter(Boolean),
          },
        };

        if (existingLessonId) {
          await updateLesson(existingLessonId, lessonPayload);
          lessonData = { id: existingLessonId, type: 'text' };
        } else {
          const newLesson = await createLesson({
            ...lessonPayload,
            id: `lesson_${courseId}_${module.id}`,
          });
          lessonData = { id: newLesson.id, type: 'text' };
        }
      } else {
        lessonData = undefined;
      }

      const updatedModule: Module = {
        ...module,
        name,
        description,
        lesson: lessonData,
        quizIds: quizId ? [quizId] : [],
      };

      onSave(updatedModule);
      toast('Сохранено');
    } catch (err) {
      window.dispatchEvent(new CustomEvent('app-toast', { detail: { message: 'Ошибка сохранения', type: 'error' } }));
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const filteredQuizzes = quizzes.filter(q =>
    q.topic.toLowerCase().includes(quizSearch.toLowerCase()) ||
    q.id.toLowerCase().includes(quizSearch.toLowerCase())
  ).slice(0, 10);

  // Переходим к тесту после загрузки урока.
  useEffect(() => {
    if (loading || initialSection !== 'quiz') return;
    const frame = requestAnimationFrame(() => document.getElementById('module-quiz')?.scrollIntoView({ block: 'start' }));
    return () => cancelAnimationFrame(frame);
  }, [loading, initialSection]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#F7F7F4]/95 flex items-center justify-center z-50">
        <div className="bg-white border border-[#E1E5E3] rounded-lg p-8">
          <div className="w-8 h-8 border-4 border-[#0F766E] border-t-transparent rounded-full animate-spin mx-auto" />
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#F7F7F4]/95 flex items-center justify-center z-50 p-2 sm:p-4">
      <AdminShell modal title="Редактор модуля" onBack={onClose} actions={
        <button onClick={handleSave} disabled={saving} className="rounded-lg bg-[#0F766E] px-3 py-2 text-sm text-white hover:bg-[#0B5F59] disabled:opacity-50">
          {saving ? 'Сохранение...' : 'Сохранить'}
        </button>
      }>
        <div className="space-y-6">
          {/* Основное */}
          <section>
            <h3 className="text-sm text-[#626B73] mb-3">Основное</h3>
            <div className="space-y-3">
              <div>
                <label className="block text-sm text-[#626B73] mb-1">Название модуля</label>
                <input
                  type="text"
                  value={name}
                  onChange={e => setName(e.target.value)}
                  className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-2 text-sm"
                  placeholder="Введение в Python"
                />
              </div>
              <div>
                <label className="block text-sm text-[#626B73] mb-1">Описание</label>
                <input
                  type="text"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-2 text-sm"
                  placeholder="Краткое описание модуля"
                />
              </div>
            </div>
          </section>

          {/* Урок */}
          <section className="border-t border-[#E1E5E3] pt-4">
            <div className="flex flex-wrap gap-2 items-center justify-between mb-3">
              <h3 className="text-sm text-[#626B73]">📖 Урок (теория)</h3>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={hasLesson}
                  onChange={e => setHasLesson(e.target.checked)}
                  className="w-4 h-4 rounded"
                />
                <span className="text-sm">Добавить урок</span>
              </label>
            </div>

            {hasLesson && (
              <div className="space-y-3 bg-[#F7F7F4] rounded-lg p-3">
                <div>
                  <label className="block text-sm text-[#626B73] mb-1">Заголовок урока</label>
                  <input
                    type="text"
                    value={lessonTitle}
                    onChange={e => setLessonTitle(e.target.value)}
                    className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-2 text-sm"
                    placeholder={name || 'Заголовок урока'}
                  />
                </div>

                <div>
                  <label className="block text-sm text-[#626B73] mb-1">Контент (Markdown)</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    <button type="button" onClick={() => photoInputRef.current?.click()} disabled={!!uploading}
                      className="px-3 py-1 bg-[#E5F2EF] hover:bg-[#F7F7F4]0 rounded text-xs disabled:opacity-50">🖼️ Фото</button>
                    <button type="button" onClick={() => videoInputRef.current?.click()} disabled={!!uploading}
                      className="px-3 py-1 bg-[#E5F2EF] hover:bg-[#F7F7F4]0 rounded text-xs disabled:opacity-50">🎬 Видео файлом</button>
                    <button type="button" onClick={onVideoLink} disabled={!!uploading}
                      className="px-3 py-1 bg-[#E5F2EF] hover:bg-[#F7F7F4]0 rounded text-xs disabled:opacity-50">🔗 Видео ссылкой</button>
                    {uploading && <span className="text-xs text-[#0F766E] self-center">Загружаю {uploading}…</span>}
                    <div className="ml-auto flex items-center gap-1 rounded-lg border border-[#E1E5E3] p-0.5">
                      {([['text', 'Текст'], ['split', 'Рядом'], ['preview', 'Как на сайте']] as [LessonView, string][]).map(([v, label]) => (
                        <button key={v} type="button" onClick={() => changeView(v)}
                          className={`rounded-md px-2.5 py-1 text-xs ${lessonView === v ? 'bg-[#0F766E] text-white' : 'text-[#626B73] hover:bg-[#E5F2EF]'} ${v === 'split' ? 'hidden lg:inline-block' : ''}`}>
                          {label}
                        </button>
                      ))}
                    </div>
                    <input ref={photoInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif,image/svg+xml" className="hidden" onChange={onPhotoChosen} />
                    <input ref={videoInputRef} type="file" accept="video/mp4,video/webm" className="hidden" onChange={onVideoChosen} />
                  </div>
                  <div className={lessonView === 'split' ? 'grid gap-3 lg:grid-cols-2' : ''}>
                    {lessonView !== 'preview' && (
                      <textarea
                        ref={textareaRef}
                        value={lessonContent}
                        onChange={e => setLessonContent(e.target.value)}
                        className="lesson-textarea w-full bg-white border border-[#E1E5E3] rounded px-3 py-2 text-sm font-mono resize-y"
                        placeholder="# Заголовок&#10;&#10;Текст урока..."
                      />
                    )}
                    {lessonView !== 'text' && (
                      <div className="lesson-preview w-full rounded border border-[#E1E5E3] bg-white px-4 py-3">
                        {previewText.trim() ? (
                          <article className="prose prose-lg prose-headings:text-[#252A2D] prose-a:text-[#0F766E] prose-strong:text-[#252A2D] max-w-none text-[#252A2D]">
                            <ReactMarkdown remarkPlugins={[remarkGfm]} components={lessonMarkdownComponents()}>{previewText}</ReactMarkdown>
                          </article>
                        ) : (
                          <p className="text-sm text-[#626B73]">Пусто: напишите текст урока слева, и здесь появится то, что увидит ученик.</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="mt-2 p-2 bg-[#F7F7F4] rounded text-xs text-[#626B73] space-y-1">
                    <p className="font-medium text-[#626B73]">💡 Форматирование:</p>
                    <p>📹 <strong>Видео</strong> — кнопки выше: файл с компьютера (заставка снимается сама) или ссылка. Вручную — адрес отдельной строкой:</p>
                    <p className="ml-4 font-mono text-[#0F766E]">https://rutube.ru/video/abc123</p>
                    <p className="ml-4 font-mono text-[#0F766E]">https://kurs.mugstel.ru/videos/курс/ролик.mp4</p>
                    <p>🖼️ <strong>Фото</strong> — кнопка «Фото» выше, или вручную:</p>
                    <p className="ml-4 font-mono text-[#0F766E]">![что на снимке](/lessons/курс/foto.jpg)</p>
                    <p>🔗 <strong>Ссылка</strong>: <span className="font-mono text-[#0F766E]">[слово](https://адрес)</span> — только на наши сайты</p>
                    <p>📊 <strong>Таблица</strong>:</p>
                    <p className="ml-4 font-mono text-amber-700">| Колонка 1 | Колонка 2 |</p>
                    <p className="ml-4 font-mono text-amber-700">|-----------|-----------|</p>
                    <p className="ml-4 font-mono text-amber-700">| Значение  | Значение  |</p>
                  </div>
                </div>

                <div>
                  <label className="block text-sm text-[#626B73] mb-1">Зачем мне это (одна фраза с результатом)</label>
                  <input value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="Через 7 минут вы сможете отличить «двойку» от «тройки» по каркасу" className="w-full mb-3 px-3 py-2 rounded-lg bg-white border border-[#E1E5E3] text-[#252A2D] text-sm" />
                  <label className="block text-sm text-[#626B73] mb-1">Теперь вы умеете (2–4 пункта)</label>
                  <div className="mb-3">
                    {outcomes.map((o, i) => (
                      <div key={i} className="flex items-center gap-2 mb-1 text-sm text-[#252A2D]">
                        <span className="min-w-0 flex-1">{o}</span>
                        <button type="button" onClick={() => setOutcomes(outcomes.filter((_, j) => j !== i))} className="text-[#626B73]">✕</button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input value={newOutcome} onChange={(e) => setNewOutcome(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && newOutcome.trim()) { e.preventDefault(); setOutcomes([...outcomes, newOutcome.trim()]); setNewOutcome(''); } }} placeholder="Добавить пункт..." className="min-w-0 flex-1 px-3 py-2 rounded-lg bg-white border border-[#E1E5E3] text-[#252A2D] text-sm" />
                      <button type="button" onClick={() => { if (newOutcome.trim()) { setOutcomes([...outcomes, newOutcome.trim()]); setNewOutcome(''); } }} className="px-3 py-2 rounded-lg bg-[#E5F2EF] text-[#252A2D] text-sm">+</button>
                    </div>
                  </div>
                  <label className="block text-sm text-[#626B73] mb-1">Ключевые тезисы</label>
                  <div className="space-y-2">
                    {keyPoints.map((point, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <span className="text-[#626B73]">•</span>
                        <span className="min-w-0 flex-1 text-sm">{point}</span>
                        <button onClick={() => removeKeyPoint(index)} className="text-[#DC2626] text-sm">✕</button>
                      </div>
                    ))}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        value={newKeyPoint}
                        onChange={e => setNewKeyPoint(e.target.value)}
                        onKeyDown={e => e.key === 'Enter' && (e.preventDefault(), addKeyPoint())}
                        className="min-w-0 flex-1 bg-white border border-[#E1E5E3] rounded px-3 py-1 text-sm"
                        placeholder="Добавить тезис..."
                      />
                      <button onClick={addKeyPoint} className="px-3 py-1 bg-[#0F766E] text-white rounded text-sm">+</button>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </section>

          {/* Тест */}
          <section id="module-quiz" className="scroll-mt-32 border-t border-[#E1E5E3] pt-4">
            <h3 className="text-sm text-[#626B73] mb-3">📋 Тест (викторина)</h3>
            <div className="space-y-2">
              <input
                type="text"
                value={quizSearch}
                onChange={e => setQuizSearch(e.target.value)}
                className="w-full bg-white border border-[#E1E5E3] rounded px-3 py-2 text-sm"
                placeholder="🔍 Поиск викторины..."
              />
              <div className="max-h-40 overflow-y-auto bg-[#F7F7F4] rounded">
                {filteredQuizzes.map(q => (
                  <div
                    key={q.id}
                    onClick={() => setQuizId(q.id)}
                    className={`px-3 py-2 cursor-pointer text-sm flex flex-wrap gap-2 items-center justify-between ${
                      quizId === q.id ? 'bg-[#E5F2EF] text-[#0F766E]' : 'hover:bg-[#E5F2EF]'
                    }`}
                  >
                    <span className="truncate">{q.categoryEmoji} {q.topic}</span>
                    <span className="text-xs text-[#626B73] ml-2">{q.questionsCount} вопр.</span>
                  </div>
                ))}
                {quizzes.length === 0 && (
                  <p className="text-[#626B73] text-xs text-center py-3">Загрузка...</p>
                )}
              </div>
              {quizId && (
                <div className="flex flex-wrap gap-2 items-center justify-between bg-[#E5F2EF] rounded px-3 py-2">
                  <span className="text-sm text-[#0F766E]">✅ {quizzes.find(q => q.id === quizId)?.topic || quizId}</span>
                  <button onClick={() => setEditingQuiz(true)} className="text-[#0F766E] text-xs mr-2">✏️ Редактировать</button>
                  <button onClick={() => setQuizId("")} className="text-[#DC2626] text-xs">✕ Убрать</button>
                </div>
              )}
            </div>
          </section>
        </div>
      </AdminShell>

      {editingQuiz && quizId && (
        <QuizEditor quizId={quizId} onClose={() => setEditingQuiz(false)} />
      )}
    </div>
  );
}